import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useCallback, useMemo } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { getUniqueTestimonials } from '@/data/testimonials';


const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const testimonials = useMemo(() => getUniqueTestimonials(), []);

  const autoplayPlugin = useRef(
    Autoplay({ delay: 7000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      containScroll: false,
      dragFree: false
    },
    [autoplayPlugin.current]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="py-20 md:py-28 bg-muted/30 overflow-hidden" ref={ref}>
      <div className="mx-auto max-w-[1280px] px-6 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-[340px_1fr] gap-12 lg:gap-16 items-start">
          
          {/* Left Column - Header Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative">
            
            <div className="mb-8">
              <p className="text-primary text-xs font-medium tracking-widest uppercase mb-3">
                Kundenstimmen
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal leading-[1.15] tracking-tight">
                Was unsere
                <br />
                Kunden{' '}
                <span className="italic text-primary">sagen</span>
              </h2>
            </div>
            
            <p className="text-muted-foreground text-sm leading-relaxed max-w-[300px] mb-8">
              Über 600 Apotheker vertrauen bereits auf unsere unabhängige Beratung.
            </p>

            <div className="flex items-center gap-3 mb-10">
              <div className="flex">
                {[...Array(5)].map((_, i) =>
                <Star key={i} className="w-[18px] h-[18px] text-primary fill-primary" />
                )}
              </div>
              <span className="text-foreground text-sm font-semibold">4,9/5</span>
              <span className="text-muted-foreground text-xs">· {testimonials.length} Bewertungen</span>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Vorherige Bewertung"
                onClick={scrollPrev}
                className="w-11 h-11 rounded-full border border-border hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all duration-300">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="w-12 h-px bg-border" />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Nächste Bewertung"
                onClick={scrollNext}
                className="w-11 h-11 rounded-full border border-border hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all duration-300">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>

          {/* Right Column - Carousel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="overflow-hidden">
            
            <div ref={emblaRef} className="overflow-hidden">
              <div className="flex gap-5">
                {testimonials.map((t, i) =>
                <div
                  key={i}
                  className="flex-shrink-0 w-[88%] sm:w-[48%] lg:w-[46%]">
                  
                    <div className="bg-card rounded-2xl p-6 md:p-7 shadow-sm border border-border/40 h-full flex flex-col min-h-[200px] relative group hover:shadow-md hover:border-border/70 transition-all duration-300">
                      {/* Quote icon */}
                      <Quote className="w-8 h-8 text-primary/10 mb-4 rotate-180" strokeWidth={1.5} />
                      
                      <p className="text-foreground text-[15px] leading-[1.7] flex-1">
                        {t.quote}
                      </p>
                      
                      <div className="mt-5 pt-4 border-t border-border/40">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-foreground text-sm font-medium">
                              {t.name}
                            </p>
                            <p className="text-muted-foreground text-xs mt-0.5">
                              {t.role && `${t.role} · `}{t.date}
                            </p>
                          </div>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, s) =>
                          <Star
                            key={s}
                            className={`w-3.5 h-3.5 ${s < t.stars ? 'text-primary fill-primary' : 'text-muted-foreground/20'}`} />

                          )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;